import type { Anlage, AnlageAktualisieren } from "../api/typen";
import {
  formatiereIban,
  normalisiereIban,
  parseLeistung,
  pruefeIban,
  pruefeLeistung,
  pruefePlz,
  pruefeTextlaenge,
  pruefeVollmachtDatum,
  pruefeZaehlernummer,
  pruefeZerezId,
} from "./validierung";

export type FeldName =
  | "leistung_kwp" | "modultyp" | "wechselrichter" | "zerez_id"
  | "strasse" | "plz" | "ort"
  | "zaehlernummer" | "iban"
  | "vollmacht_unterschrieben" | "vollmacht_datum";

/** Formularzustand: alles als String, damit Eingaben wie „9,86“ möglich sind. */
export type FormWerte = Record<FeldName, string>;

export interface FeldDefinition {
  name: FeldName;
  label: string;
  art: "text" | "zahl" | "datum" | "janein";
  platzhalter?: string;
  einheit?: string;
  pflicht?: boolean;
  pruefen: (wert: string) => string | null;
}

export interface FeldGruppe {
  titel: string;
  felder: FeldDefinition[];
}

export const FELD_GRUPPEN: FeldGruppe[] = [
  {
    titel: "Anlage",
    felder: [
      { name: "leistung_kwp", label: "Leistung", art: "zahl", einheit: "kWp", platzhalter: "z. B. 9,86", pflicht: true, pruefen: pruefeLeistung },
      { name: "modultyp", label: "Modultyp", art: "text", platzhalter: "Hersteller + Modell", pruefen: (w) => pruefeTextlaenge(w) },
      { name: "wechselrichter", label: "Wechselrichter", art: "text", platzhalter: "Hersteller + Modell", pruefen: (w) => pruefeTextlaenge(w) },
      { name: "zerez_id", label: "ZEREZ-ID", art: "text", platzhalter: "optional", pruefen: pruefeZerezId },
    ],
  },
  {
    titel: "Standort",
    felder: [
      { name: "strasse", label: "Straße + Nr.", art: "text", pflicht: true, pruefen: (w) => pruefeTextlaenge(w) },
      { name: "plz", label: "PLZ", art: "text", platzhalter: "5 Ziffern", pflicht: true, pruefen: pruefePlz },
      { name: "ort", label: "Ort", art: "text", pflicht: true, pruefen: (w) => pruefeTextlaenge(w, 100) },
    ],
  },
  {
    titel: "Netz & Vergütung",
    felder: [
      { name: "zaehlernummer", label: "Zählernummer", art: "text", pflicht: true, pruefen: pruefeZaehlernummer },
      { name: "iban", label: "IBAN", art: "text", platzhalter: "DE00 0000 0000 0000 0000 00", pruefen: pruefeIban },
    ],
  },
  {
    titel: "Vollmacht",
    felder: [
      { name: "vollmacht_unterschrieben", label: "Unterschrieben", art: "janein", pruefen: () => null },
      { name: "vollmacht_datum", label: "Datum der Vollmacht", art: "datum", pruefen: (w) => pruefeVollmachtDatum(w) },
    ],
  },
];

export const ALLE_FELDER: FeldDefinition[] = FELD_GRUPPEN.flatMap((g) => g.felder);
export const FELD_LABEL: Record<string, string> = Object.fromEntries(ALLE_FELDER.map((f) => [f.name, f.label]));

export function formAusAnlage(anlage: Anlage): FormWerte {
  const text = (w: string | null) => w ?? "";
  return {
    leistung_kwp: anlage.leistung_kwp === null ? "" : String(anlage.leistung_kwp).replace(".", ","),
    modultyp: text(anlage.modultyp),
    wechselrichter: text(anlage.wechselrichter),
    zerez_id: text(anlage.zerez_id),
    strasse: text(anlage.strasse),
    plz: text(anlage.plz),
    ort: text(anlage.ort),
    zaehlernummer: text(anlage.zaehlernummer),
    iban: anlage.iban ? formatiereIban(anlage.iban) : "",
    vollmacht_unterschrieben: anlage.vollmacht_unterschrieben === null ? "" : anlage.vollmacht_unterschrieben ? "ja" : "nein",
    vollmacht_datum: text(anlage.vollmacht_datum),
  };
}

/** Wert im API-Format (für Vergleich und PUT-Body). */
export function apiWert(name: FeldName, roh: string): string | number | boolean | null {
  const wert = roh.trim();
  if (!wert) return null;
  switch (name) {
    case "leistung_kwp":
      return parseLeistung(wert);
    case "iban":
      return normalisiereIban(wert);
    case "zaehlernummer":
      return wert.replace(/\s/g, "").toUpperCase();
    case "plz":
      return wert.replace(/\s/g, "");
    case "vollmacht_unterschrieben":
      return wert === "ja";
    default:
      return wert.replace(/\s+/g, " ");
  }
}

export function validiereFormular(werte: FormWerte): Partial<Record<FeldName, string>> {
  const fehler: Partial<Record<FeldName, string>> = {};
  for (const feld of ALLE_FELDER) {
    const meldung = feld.pruefen(werte[feld.name]);
    if (meldung) fehler[feld.name] = meldung;
  }
  return fehler;
}

/** Nur geänderte Felder → PUT-Body. */
export function geaenderteFelder(ausgang: FormWerte, aktuell: FormWerte): FeldName[] {
  return ALLE_FELDER.map((f) => f.name).filter((name) => apiWert(name, ausgang[name]) !== apiWert(name, aktuell[name]));
}

export function putBody(aktuell: FormWerte, felder: FeldName[], version: number): AnlageAktualisieren {
  const body: Record<string, unknown> = { version };
  for (const name of felder) body[name] = apiWert(name, aktuell[name]);
  return body as AnlageAktualisieren;
}
